import React, { useState, useCallback, useEffect } from "react";

/**
 * Add one line per preset you host under /public/assets/emitter/presets/
 * Each preset file must export default { ... } (your existing format).
 */
const PRESETS = [
  { file: "rain.js", label: "Rain" },
  { file: "fire_cone.js", label: "Fire Cone" },
  { file: "fire_missile.js", label: "Fire Missile" },
  // { file: "snow.js", label: "Snow" },
];

// Persisted open/closed state key
const OPEN_KEY = "EmitterTool:PresetsPanelOpenV1";

function CollapsibleBox({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(OPEN_KEY);
      return saved == null ? defaultOpen : saved === "1";
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch {}
  }, [open]);

  return (
    <div
      style={{
        border: "1px solid #334155",
        borderRadius: 8,
        marginBottom: 12,
        overflow: "hidden",
        background: "#0b0f17",
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "8px 10px",
          background: "#18212f",
          userSelect: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
        }}
      >
        <span>{title}</span>
        <span style={{ opacity: 0.75 }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div style={{ padding: 8 }}>{children}</div>}
    </div>
  );
}

/**
 * This component supports TWO ways of applying a server preset:
 *   1) Preferred: pass `applyPresetText(text)` — raw JS/JSON text is sent up and
 *      the parent parses & applies it (unified with file-import path).
 *   2) Back-compat: if `applyPresetText` is not provided, we fallback to
 *      dynamic-import parsing here and call `applyPreset(object)`.
 */
export default function PresetsPanel({
  baseUrl = "/assets/emitter/presets",
  title = "Presets",
  defaultOpen = true,
  // NEW (preferred): parent parses text via the same hook as Import
  applyPresetText,
  // Legacy (still supported): parent receives parsed object
  applyPreset,
}) {
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);

  const loadFromServer = useCallback(
    async (file) => {
      setError(null);
      setLoading(file);
      try {
        const url = `${baseUrl}/${file}?t=${Date.now()}`;
        console.log("[Presets] fetching", url);
        const res = await fetch(url, { credentials: "same-origin", cache: "no-cache" });
        if (!res.ok) throw new Error(`HTTP ${res.status} while fetching ${url}`);
        const js = await res.text();
        console.log("[Presets] fetched bytes:", js.length, "preview:", js.slice(0, 80));

        // Preferred: send raw text up to parent for unified parse/apply
        if (typeof applyPresetText === "function") {
          await applyPresetText(js);
          return;
        }

        // Fallback: parse here (for back-compat with old API)
        const dataUrl = "data:text/javascript;charset=utf-8," + encodeURIComponent(js);
        const mod = await import(/* webpackIgnore: true */ dataUrl);
        const obj = mod?.default ?? mod;
        if (!obj || typeof obj !== "object") {
          throw new Error("Preset file did not export a default object.");
        }

        if (typeof applyPreset === "function") {
          console.log("[Presets] applying preset object:", obj?.name || file);
          applyPreset(obj);
        } else {
          console.warn("[Presets] Neither applyPresetText nor applyPreset provided.");
        }
      } catch (e) {
        console.error("[Presets] failed:", e);
        setError(e?.message || String(e));
      } finally {
        setLoading(null);
      }
    },
    [applyPresetText, applyPreset, baseUrl]
  );

  return (
    <CollapsibleBox title={title} defaultOpen={defaultOpen}>
      {PRESETS.map((p) => (
        <button
          key={p.file}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            loadFromServer(p.file);
          }}
          disabled={loading === p.file}
          style={{
            width: "100%",
            textAlign: "left",
            display: "flex",
            gap: 12,
            alignItems: "center",
            padding: "6px 8px",
            marginBottom: 6,
            border: "1px solid #374151",
            borderRadius: 6,
            background: "transparent",
            color: "inherit",
            cursor: "pointer",
          }}
          title={`Load preset from ${baseUrl}/${p.file}`}
        >
          <code style={{ opacity: 0.85, fontSize: 12 }}>{p.file}</code>
          <span style={{ opacity: 0.7, fontSize: 12 }}>— {p.label}</span>
          {loading === p.file && (
            <span style={{ marginLeft: "auto", fontSize: 12, opacity: 0.7 }}>
              loading…
            </span>
          )}
        </button>
      ))}
      {error && (
        <div style={{ color: "#f87171", fontSize: 12, marginTop: 6 }}>
          {error}
        </div>
      )}
    </CollapsibleBox>
  );
}

export { PresetsPanel };
